import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, FileText, FileSpreadsheet, FileCode, Copy, Link2, Code2 } from 'lucide-react';
import styles from './ExportMenu.module.css';

export type ExportFormat = 'pdf' | 'md' | 'csv' | 'dimensions-csv' | 'json' | 'summary';

interface Props {
  onExport: (format: ExportFormat) => void;
  onShare: () => void;
  onViewSource: () => void;
  className?: string;
}

interface MenuItem {
  key: string;
  label: string;
  icon: typeof FileText;
  group: 'download' | 'copy' | 'share' | 'debug';
  onSelect: () => void;
  shortcut?: string;
}

export function ExportMenu({ onExport, onShare, onViewSource, className }: Props) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  const items: MenuItem[] = [
    { key: 'pdf', label: 'Download PDF', icon: FileText, group: 'download', onSelect: () => onExport('pdf') },
    { key: 'md', label: 'Download Markdown', icon: FileText, group: 'download', onSelect: () => onExport('md') },
    { key: 'csv', label: 'Download CSV', icon: FileSpreadsheet, group: 'download', onSelect: () => onExport('csv') },
    { key: 'dimensions-csv', label: 'Download Dimensions CSV', icon: FileSpreadsheet, group: 'download', onSelect: () => onExport('dimensions-csv') },
    { key: 'summary', label: 'Copy Summary', icon: Copy, group: 'copy', onSelect: () => onExport('summary') },
    { key: 'json', label: 'Copy JSON', icon: FileCode, group: 'copy', onSelect: () => onExport('json') },
    { key: 'share', label: 'Share Link', icon: Link2, group: 'share', onSelect: onShare },
  ];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(items.length - 1, i + 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        items[activeIdx]?.onSelect();
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, activeIdx, items]);

  const groups: { id: 'download' | 'copy' | 'share'; title: string }[] = [
    { id: 'download', title: 'Download' },
    { id: 'copy', title: 'Copy' },
    { id: 'share', title: 'Share' },
  ];

  return (
    <div ref={ref} className={[styles.wrap, className].filter(Boolean).join(' ')}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => {
          setOpen((o) => !o);
          setActiveIdx(0);
        }}
      >
        <span>Export</span>
        <ChevronDown size={16} strokeWidth={1.5} aria-hidden="true" />
      </button>
      {open && (
        <div id={id} role="menu" aria-label="Export options" className={styles.menu}>
          {groups.map((g) => {
            const groupItems = items.filter((i) => i.group === g.id);
            if (!groupItems.length) return null;
            return (
              <div key={g.id} className={styles.group}>
                <div className={styles.groupTitle}>{g.title}</div>
                {groupItems.map((it) => {
                  const idx = items.indexOf(it);
                  const Icon = it.icon;
                  return (
                    <button
                      key={it.key}
                      type="button"
                      role="menuitem"
                      className={[styles.item, activeIdx === idx ? styles.active : ''].join(' ')}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => {
                        it.onSelect();
                        setOpen(false);
                        buttonRef.current?.focus();
                      }}
                    >
                      <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
                      <span>{it.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          <div className={styles.footer}>
            <button
              type="button"
              role="menuitem"
              className={styles.viewSource}
              onClick={() => {
                onViewSource();
                setOpen(false);
                buttonRef.current?.focus();
              }}
            >
              <Code2 size={14} strokeWidth={1.5} aria-hidden="true" />
              View source
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
